/**
 * Cart Drawer — Upsell Quick Add + Shipping Bar (Fixed)
 * assets/component-cart-upsell.js
 *
 * Root cause fix:
 *  After /cart/add.js succeeds, we now re-fetch the cart SECTION HTML
 *  using Shopify's Section Rendering API and swap the cart items container
 *  in the DOM — so the newly added product appears immediately without
 *  any manual refresh.
 *
 * How Section Rendering API works:
 *  GET /?sections=<section-id>
 *  Returns JSON: { "<section-id>": "<rendered HTML string>" }
 *  We extract the relevant DOM nodes and swap them in.
 */

(function () {
  'use strict';

  const FREE_SHIPPING_THRESHOLD = 15000; // cents ($150.00) — keep in sync with Liquid

  /* ─────────────────────────────────────────────────────────────
   *  SECTION ID DETECTION
   *  The mini-cart is rendered as a Shopify section. We need its
   *  section ID to call the Section Rendering API.
   *  Strategy 1: read data-section-id from the drawer element
   *  Strategy 2: read from the shopify-section wrapper div
   *  Strategy 3: fallback to common theme section names
   * ───────────────────────────────────────────────────────────── */
  function getCartSectionId() {
    // Most themes wrap sections in <div id="shopify-section-{id}">
    const drawerWrapper = document.querySelector(
      '[id^="shopify-section-"]:has(#mini-cart), ' +
      '[id^="shopify-section-"]:has(.mini-cart)'
    );
    if (drawerWrapper) {
      return drawerWrapper.id.replace('shopify-section-', '');
    }

    // Read from data attribute if theme sets it
    const drawer = document.querySelector('#mini-cart, .mini-cart');
    if (drawer) {
      const sectionId = drawer.closest('[data-section-id]')?.dataset.sectionId
        || drawer.dataset.sectionId;
      if (sectionId) return sectionId;
    }

    // Common fallback IDs used by popular Shopify themes
    const fallbacks = ['cart-drawer', 'mini-cart', 'cart', 'cart-notification'];
    for (const id of fallbacks) {
      if (document.getElementById('shopify-section-' + id)) return id;
    }

    return null;
  }

  /* ─────────────────────────────────────────────────────────────
   *  RE-RENDER CART SECTION via Section Rendering API
   *  Fetches fresh HTML for the entire cart section, then surgically
   *  replaces only the cart items container and the order summary
   *  so the rest of the drawer (close button, upsell, etc.) stays.
   * ───────────────────────────────────────────────────────────── */
  function rerenderCartSection(sectionId) {
    return fetch('/?sections=' + encodeURIComponent(sectionId), {
      headers: { 'Accept': 'application/json' }
    })
    .then(res => {
      if (!res.ok) throw new Error('Section render failed: ' + res.status);
      return res.json();
    })
    .then(data => {
      const html = data[sectionId];
      if (!html) throw new Error('Section not found in response: ' + sectionId);

      // Parse the returned HTML into a temporary DOM
      const parser  = new DOMParser();
      const newDoc  = parser.parseFromString(html, 'text/html');

      // ── Swap cart items container ────────────────────────────
      const newItems  = newDoc.querySelector('.cart-items-container');
      const liveItems = document.querySelector('.cart-items-container');
      if (newItems && liveItems) {
        liveItems.innerHTML = newItems.innerHTML;
      }

      // ── Swap order summary (totals, subtotal) ────────────────
      const newSummary  = newDoc.querySelector('.mini-cart__footer');
      const liveSummary = document.querySelector('.mini-cart__footer');
      if (newSummary && liveSummary) {
        liveSummary.innerHTML = newSummary.innerHTML;
      }

      // ── Swap upsell list (removes added product) ─────────────
      const newUpsell  = newDoc.querySelector('[data-upsell-section]');
      const liveUpsell = document.querySelector('[data-upsell-section]');
      if (newUpsell && liveUpsell) {
        liveUpsell.innerHTML = newUpsell.innerHTML;
      } else if (!newUpsell && liveUpsell) {
        // All upsell products added — hide the section
        liveUpsell.style.display = 'none';
      }

      // ── Handle empty cart state ──────────────────────────────
      const newEmpty  = newDoc.querySelector('.mini-cart--empty');
      const liveContent = document.querySelector('.mini-cart__content');
      const newContent  = newDoc.querySelector('.mini-cart__content');
      if (newEmpty && liveContent && newContent) {
        liveContent.innerHTML = newContent.innerHTML;
      }
    });
  }

  /* ─────────────────────────────────────────────────────────────
   *  DIRECT DOM UPDATE FALLBACK
   *  If Section Rendering API doesn't work (section ID not found,
   *  or theme doesn't support it), we manually build and inject
   *  the new cart item using the cart.js data.
   * ───────────────────────────────────────────────────────────── */
  function buildCartItemHTML(item) {
    const imgSrc = item.image
      ? item.image.replace('http:', 'https:')
      : '';

    return `
      <div class="cart-item" data-cart-item data-key="${item.key}">
        <div class="cart-item__media">
          ${imgSrc
            ? `<a href="${item.url}"><img src="${imgSrc}" alt="${item.title}" width="80" height="80" loading="lazy"></a>`
            : '<div class="cart-item__img-placeholder"></div>'
          }
        </div>
        <div class="cart-item__details">
          <a href="${item.url}" class="cart-item__name">${item.product_title}</a>
          ${item.variant_title && item.variant_title !== 'Default Title'
            ? `<p class="cart-item__variant">${item.variant_title}</p>`
            : ''
          }
          <p class="cart-item__price">${formatMoney(item.final_line_price)}</p>
          <div class="cart-item__quantity">
            <button type="button" class="cart-item__qty-btn" data-change="-1" data-key="${item.key}">−</button>
            <span>${item.quantity}</span>
            <button type="button" class="cart-item__qty-btn" data-change="1" data-key="${item.key}">+</button>
          </div>
        </div>
      </div>
    `;
  }

  function updateCartDOMDirectly(cart) {
    const container = document.querySelector('.cart-items-container');
    if (!container) return;

    // Check if theme renders its own cart items — if so, don't overwrite
    if (container.querySelector('[data-cart-item]')) {
      // Theme already has rendered items — append new items that aren't there
      const existingKeys = Array.from(container.querySelectorAll('[data-cart-item]'))
        .map(el => el.dataset.key);

      cart.items.forEach(item => {
        if (!existingKeys.includes(item.key)) {
          const temp = document.createElement('div');
          temp.innerHTML = buildCartItemHTML(item).trim();
          const newItem = temp.firstElementChild;
          // Animate in
          newItem.style.opacity = '0';
          newItem.style.transform = 'translateY(8px)';
          container.appendChild(newItem);
          requestAnimationFrame(() => {
            newItem.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
            newItem.style.opacity    = '1';
            newItem.style.transform  = 'translateY(0)';
          });
        }
      });
    }
  }

  /* ─────────────────────────────────────────────────────────────
   *  UPDATE SUBTOTAL TEXT
   *  Even without full re-render, update the visible subtotal
   * ───────────────────────────────────────────────────────────── */
  function updateSubtotal(cartTotal) {
    document.querySelectorAll(
      '[data-cart-subtotal], .cart-subtotal__price, .js-cart-subtotal'
    ).forEach(el => {
      el.textContent = formatMoney(cartTotal);
    });
  }

  /* ─────────────────────────────────────────────────────────────
   *  SHIPPING BAR UPDATE
   * ───────────────────────────────────────────────────────────── */
  function updateShippingBar(cartTotal) {
    const fill    = document.querySelector('[data-shipping-fill]');
    const message = document.querySelector('[data-shipping-message]');
    if (!fill || !message) return;

    const pct       = Math.min((cartTotal / FREE_SHIPPING_THRESHOLD) * 100, 100);
    const remaining = FREE_SHIPPING_THRESHOLD - cartTotal;
    const qualified = cartTotal >= FREE_SHIPPING_THRESHOLD;

    fill.style.width = pct + '%';
    fill.classList.toggle('cart-shipping-bar__fill--complete', qualified);

    if (qualified) {
      message.innerHTML = `
        <span class="cart-shipping-bar__icon">
          <span class="icon material-symbols-outlined" style="font-size:16px">local_shipping</span>
        </span>
        You've unlocked free shipping!
      `;
    } else {
      message.innerHTML = `
        <span class="cart-shipping-bar__icon">
          <span class="icon material-symbols-outlined" style="font-size:16px">local_shipping</span>
        </span>
        Add ${formatMoney(remaining)} more for free shipping
      `;
    }
  }

  /* ─────────────────────────────────────────────────────────────
   *  CART COUNT BADGE
   * ───────────────────────────────────────────────────────────── */
  function updateCartCount(count) {
    document.querySelectorAll(
      '[data-cart-count], .cart-count, .js-cart-count, ' +
      '.header__cart-count, [aria-label*="cart"] .count'
    ).forEach(el => {
      el.textContent = count;
    });
  }

  /* ─────────────────────────────────────────────────────────────
   *  MAIN: AFTER SUCCESSFUL ADD TO CART
   * ───────────────────────────────────────────────────────────── */
  function fetchCartAndUpdate(button) {
    const sectionId = getCartSectionId();

    // Show success state on button immediately
    if (button) {
      button.setAttribute('data-state', 'done');
    }

    // Fetch current cart data first
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
    .then(res => res.json())
    .then(cart => {
      // Always update these regardless of re-render method
      updateShippingBar(cart.total_price);
      updateSubtotal(cart.total_price);
      updateCartCount(cart.item_count);

      // Dispatch for theme's own listeners
      document.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart }
      }));

      // ── Primary: Section Rendering API ──────────────────────
      if (sectionId) {
        return rerenderCartSection(sectionId).catch(err => {
          // Section render failed — fall back to direct DOM update
          console.warn('[Cart Upsell] Section render failed, using DOM fallback:', err);
          updateCartDOMDirectly(cart);
          removeUpsellItem(button);
        });
      }

      // ── Fallback: direct DOM injection ──────────────────────
      updateCartDOMDirectly(cart);
      removeUpsellItem(button);
    })
    .catch(err => {
      console.error('[Cart Upsell] fetchCartAndUpdate error:', err);
      // Reset button so user can try again
      if (button) {
        button.removeAttribute('data-state');
        button.removeAttribute('disabled');
      }
    });
  }

  /* ─────────────────────────────────────────────────────────────
   *  ANIMATE + REMOVE UPSELL CARD
   * ───────────────────────────────────────────────────────────── */
  function removeUpsellItem(button) {
    if (!button) return;
    const item = button.closest('[data-upsell-item]');
    if (!item) return;

    setTimeout(() => {
      const height = item.offsetHeight;
      item.style.transition = 'opacity 0.25s ease, transform 0.25s ease, max-height 0.35s ease, margin 0.35s ease, padding 0.35s ease';
      item.style.opacity    = '0';
      item.style.transform  = 'translateX(12px)';
      item.style.maxHeight  = height + 'px';
      item.style.overflow   = 'hidden';

      requestAnimationFrame(() => {
        setTimeout(() => {
          item.style.maxHeight = '0';
          item.style.margin    = '0';
          item.style.padding   = '0';
        }, 50);
      });

      setTimeout(() => {
        item.remove();
        // Hide upsell section if no items remain
        const list = document.querySelector('[data-upsell-list]');
        if (list && list.children.length === 0) {
          const section = document.querySelector('[data-upsell-section]');
          if (section) {
            section.style.transition = 'opacity 0.3s ease';
            section.style.opacity    = '0';
            setTimeout(() => section.remove(), 300);
          }
        }
      }, 380);
    }, 600);
  }

  /* ─────────────────────────────────────────────────────────────
   *  QUICK ADD HANDLER
   * ───────────────────────────────────────────────────────────── */
  function handleQuickAdd(button) {
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.setAttribute('data-state', 'loading');
    button.setAttribute('disabled', '');

    fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body:    JSON.stringify({
        items: [{ id: parseInt(variantId, 10), quantity: 1 }],
        sections: getCartSectionId() || undefined
      })
    })
    .then(res => {
      if (!res.ok) {
        return res.json().then(err => { throw new Error(err.description || 'Add failed'); });
      }
      return res.json();
    })
    .then(data => {
      // /cart/add.js with sections= returns section HTML directly
      // Try to use it before making another fetch
      if (data.sections && getCartSectionId() && data.sections[getCartSectionId()]) {
        const sectionId = getCartSectionId();
        const html      = data.sections[sectionId];
        const parser    = new DOMParser();
        const newDoc    = parser.parseFromString(html, 'text/html');

        // Swap cart items
        const newItems  = newDoc.querySelector('.cart-items-container');
        const liveItems = document.querySelector('.cart-items-container');
        if (newItems && liveItems) liveItems.innerHTML = newItems.innerHTML;

        // Swap footer/summary
        const newFooter  = newDoc.querySelector('.mini-cart__footer');
        const liveFooter = document.querySelector('.mini-cart__footer');
        if (newFooter && liveFooter) liveFooter.innerHTML = newFooter.innerHTML;

        // Update shipping bar from cart data in response
        if (data.cart) {
          updateShippingBar(data.cart.total_price);
          updateSubtotal(data.cart.total_price);
          updateCartCount(data.cart.item_count);
        }

        button.setAttribute('data-state', 'done');
        lastRelatedKey = null;
        scheduleRelatedLoad();
        removeUpsellItem(button);
        return;
      }

      // No sections data in response — fetch separately
      fetchCartAndUpdate(button);
    })
    .catch(err => {
      console.error('[Cart Upsell] Quick add error:', err);
      button.removeAttribute('data-state');
      button.removeAttribute('disabled');
    });
  }

  /* ─────────────────────────────────────────────────────────────
   *  MONEY FORMATTER
   * ───────────────────────────────────────────────────────────── */
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  /* ─────────────────────────────────────────────────────────────
   *  FETCH CART AND REFRESH SHIPPING BAR ONLY
   *  Lightweight version — just re-fetches /cart.js and updates
   *  the bar. Used when theme handles its own DOM re-render.
   * ───────────────────────────────────────────────────────────── */
  function refreshShippingBar() {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
    .then(res => res.json())
    .then(cart => {
      updateShippingBar(cart.total_price);
      updateSubtotal(cart.total_price);
      updateCartCount(cart.item_count);
    })
    .catch(() => {});
  }

  /* ─────────────────────────────────────────────────────────────
   *  INTERCEPT NATIVE FETCH — catch /cart/change.js and
   *  /cart/update.js calls made by the theme's own cart component
   *  so we can refresh the shipping bar after any cart mutation.
   * ───────────────────────────────────────────────────────────── */
  (function interceptCartFetch() {
    const _fetch = window.fetch;
    window.fetch = function (input, init) {
      const url = (typeof input === 'string' ? input : input?.url) || '';

      // Detect cart mutation endpoints
      const isCartChange = /\/cart\/(change|update|add)\.js/.test(url);

      if (isCartChange) {
        return _fetch.apply(this, arguments).then(function (response) {
          const clone = response.clone();
          const isAdd = /\/cart\/add\.js/.test(url);
          clone.json()
            .then(data => {
              const total = data.total_price !== undefined
                ? data.total_price
                : null;

              if (total !== null) {
                updateShippingBar(total);
                updateSubtotal(total);
                updateCartCount(data.item_count);
              }

              if (isAdd) {
                const productId = data.product_id
                  || (data.items && data.items[0] && data.items[0].product_id)
                  || null;
                scheduleRelatedLoad(productId);
              }
            })
            .catch(() => {
              setTimeout(refreshShippingBar, 300);
              if (isAdd) scheduleRelatedLoad();
            });
          return response;
        });
      }

      return _fetch.apply(this, arguments);
    };
  })();

  /* ─────────────────────────────────────────────────────────────
   *  INTERCEPT XMLHttpRequest — some themes use XHR instead of
   *  fetch for cart operations (older Debut, Brooklyn, etc.)
   * ───────────────────────────────────────────────────────────── */
  (function interceptCartXHR() {
    const _open = XMLHttpRequest.prototype.open;
    const _send = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this._cartUrl = url;
      return _open.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      const url = this._cartUrl || '';
      if (/\/cart\/(change|update)\.js/.test(url)) {
        this.addEventListener('load', function () {
          try {
            const data = JSON.parse(this.responseText);
            if (data.total_price !== undefined) {
              updateShippingBar(data.total_price);
              updateSubtotal(data.total_price);
              updateCartCount(data.item_count);
            }
          } catch (e) {
            setTimeout(refreshShippingBar, 300);
          }
        });
      }
      return _send.apply(this, arguments);
    };
  })();

  /* ─────────────────────────────────────────────────────────────
   *  THEME CUSTOM EVENTS — listen to every common event name
   *  used by popular Shopify themes to signal cart changes
   * ───────────────────────────────────────────────────────────── */
  const CART_EVENTS = [
    'cart:updated',       // Generic / our own
    'cart:refresh',       // Dawn, Impulse
    'cart:change',        // Prestige, Warehouse
    'cart:item-removed',  // Various themes
    'cart:item-added',    // Various themes
    'on:cart:update',     // Streamline
    'theme:cart:update',  // Focal, some premium themes
    'CartUpdate',         // Legacy themes
    'updated.ajaxCart',   // jQuery AJAX Cart plugin
  ];

  CART_EVENTS.forEach(function (eventName) {
    document.addEventListener(eventName, function (e) {
      const cart = e.detail?.cart || e.detail;
      if (cart && cart.total_price !== undefined) {
        updateShippingBar(cart.total_price);
        updateSubtotal(cart.total_price);
        updateCartCount(cart.item_count);
      } else {
        refreshShippingBar();
      }
      scheduleRelatedLoad();
    });
  });

  /* ─────────────────────────────────────────────────────────────
   *  MUTATION OBSERVER — last resort
   *  Watches the cart items container for DOM changes (item added
   *  or removed by theme JS). Debounced to avoid rapid-fire calls.
   * ───────────────────────────────────────────────────────────── */
  let mutationTimer = null;

  function startCartObserver() {
    const target = document.querySelector(
      '.cart-items-container, .mini-cart__content, [data-cart-items]'
    );
    if (!target) return;

    const observer = new MutationObserver(function () {
      clearTimeout(mutationTimer);
      mutationTimer = setTimeout(refreshShippingBar, 250);
    });

    observer.observe(target, { childList: true, subtree: true });
  }

  /* ─────────────────────────────────────────────────────────────
   *  CLICK HANDLER — Quick Add buttons
   * ───────────────────────────────────────────────────────────── */
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    if (btn.disabled || btn.getAttribute('data-state') === 'loading') return;
    e.preventDefault();
    handleQuickAdd(btn);
  });

  /* ─────────────────────────────────────────────────────────────
   *  RELATED PRODUCT CAROUSEL
   * ───────────────────────────────────────────────────────────── */
  let relatedTimer = null;
  let lastRelatedKey = null;

  function scheduleRelatedLoad(productId) {
    clearTimeout(relatedTimer);
    relatedTimer = setTimeout(function () {
      loadRelatedCarousel(productId);
    }, 250);
  }

  function toCents(value) {
    if (value == null || value === '') return 0;
    if (typeof value === 'number') {
      return Number.isInteger(value) ? value : Math.round(value * 100);
    }
    const str = String(value);
    if (str.indexOf('.') !== -1) return Math.round(parseFloat(str) * 100);
    return parseInt(str, 10) || 0;
  }

  function formatRelatedMoney(cents, moneyFormat) {
    cents = Math.round(Number(cents) || 0);
    const amount = (cents / 100).toFixed(2);
    const format = moneyFormat || '${{amount}}';
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, format);
    }
    return format.replace(/\{\{\s*amount\s*\}\}/g, amount);
  }

  function imageUrl(product) {
    const img = product.featured_image
      || (product.images && product.images[0])
      || (product.featured_image && product.featured_image.src);
    if (!img) return '';
    if (typeof img === 'string') return img.indexOf('//') === 0 ? 'https:' + img : img;
    if (img.src) return img.src.indexOf('//') === 0 ? 'https:' + img.src : img.src;
    return '';
  }

  function bindCarouselNav(root) {
    if (root.dataset.navBound === 'true') return;
    root.dataset.navBound = 'true';
    const list = root.querySelector('[data-upsell-list]');
    const prev = root.querySelector('[data-related-prev]');
    const next = root.querySelector('[data-related-next]');
    if (!list) return;

    function amount() {
      return Math.max(list.clientWidth * 0.8, 160);
    }

    if (prev) {
      prev.addEventListener('click', function (e) {
        e.preventDefault();
        list.scrollBy({ left: -amount(), behavior: 'smooth' });
      });
    }
    if (next) {
      next.addEventListener('click', function (e) {
        e.preventDefault();
        list.scrollBy({ left: amount(), behavior: 'smooth' });
      });
    }
  }

  function cardHTML(product, moneyFormat) {
    const variant = (product.variants || []).find(function (v) { return v.available; })
      || (product.variants && product.variants[0]);
    if (!variant) return '';

    const img = imageUrl(product);
    const url = product.url || ('/products/' + product.handle);
    const price = toCents(product.price != null ? product.price : variant.price);
    const compare = toCents(product.compare_at_price);
    const available = variant.available !== false;
    const title = product.title || '';
    const escaped = title.replace(/"/g, '&quot;');

    let priceHtml = '<span class="cart-upsell__price">' + formatRelatedMoney(price, moneyFormat) + '</span>';
    if (compare > price) {
      priceHtml = '<span class="cart-upsell__price cart-upsell__price--sale">' + formatRelatedMoney(price, moneyFormat) + '</span>'
        + '<span class="cart-upsell__price cart-upsell__price--compare">' + formatRelatedMoney(compare, moneyFormat) + '</span>';
    }

    return '<div class="cart-upsell__item" data-upsell-item data-product-id="' + product.id + '">'
      + '<a href="' + url + '" class="cart-upsell__img-wrap" tabindex="-1">'
      + (img
        ? '<img src="' + img + '" alt="' + escaped + '" width="180" height="180" loading="lazy" class="cart-upsell__img">'
        : '<div class="cart-upsell__img-placeholder"></div>')
      + '</a>'
      + '<div class="cart-upsell__info">'
      + '<a href="' + url + '" class="cart-upsell__title">' + title.replace(/</g, '&lt;') + '</a>'
      + '<div class="cart-upsell__meta">' + priceHtml + '</div>'
      + '<button type="button" class="cart-upsell__quick-add" data-quick-add data-variant-id="' + variant.id + '" data-product-title="' + escaped + '" '
      + (available ? '' : 'disabled ')
      + 'aria-label="Add: ' + escaped + '">'
      + '<span class="cart-upsell__quick-add-text">' + (available ? 'Add' : 'Sold out') + '</span>'
      + '</button>'
      + '</div></div>';
  }

  function fetchRecommendations(productId, limit) {
    const url = '/recommendations/products.json?product_id=' + encodeURIComponent(productId)
      + '&limit=' + encodeURIComponent(limit)
      + '&intent=related';
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : { products: [] }; })
      .then(function (data) {
        if (data.products && data.products.length) return data.products;
        const complementary = '/recommendations/products.json?product_id=' + encodeURIComponent(productId)
          + '&limit=' + encodeURIComponent(limit)
          + '&intent=complementary';
        return fetch(complementary, { headers: { Accept: 'application/json' } })
          .then(function (res) { return res.ok ? res.json() : { products: [] }; })
          .then(function (alt) { return alt.products || []; });
      });
  }

  function loadRelatedCarousel(productId) {
    const root = document.querySelector('[data-related-carousel]');
    if (!root) return;

    bindCarouselNav(root);

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        if (!cart || !cart.item_count) {
          root.hidden = true;
          lastRelatedKey = null;
          return null;
        }

        const cartIds = cart.items.map(function (item) { return String(item.product_id); });
        const pid = productId
          || cart.items[cart.items.length - 1].product_id
          || cart.items[0].product_id;

        root.dataset.productId = pid;
        root.dataset.cartIds = cartIds.join(',');

        const list = root.querySelector('[data-upsell-list]');
        const relatedKey = String(pid) + ':' + cartIds.slice().sort().join(',');
        const existingCards = list ? list.querySelectorAll('[data-upsell-item]').length : 0;
        if (relatedKey === lastRelatedKey && existingCards) {
          root.hidden = false;
          return null;
        }

        lastRelatedKey = relatedKey;
        const limit = parseInt(root.dataset.limit, 10) || 8;
        const moneyFormat = root.dataset.moneyFormat || '${{amount}}';

        return fetchRecommendations(pid, limit).then(function (products) {
          const filtered = (products || []).filter(function (product) {
            return cartIds.indexOf(String(product.id)) === -1;
          });

          if (filtered.length && list) {
            list.innerHTML = filtered.map(function (product) {
              return cardHTML(product, moneyFormat);
            }).join('');
          }

          const hasCards = list && list.querySelectorAll('[data-upsell-item]').length > 0;
          root.hidden = !hasCards;
        });
      })
      .catch(function () {
        const list = root.querySelector('[data-upsell-list]');
        root.hidden = !(list && list.children.length);
      });
  }

  document.addEventListener('DOMContentLoaded', function () {
    refreshShippingBar();
    startCartObserver();
    scheduleRelatedLoad();
  });

  if (document.readyState !== 'loading') {
    refreshShippingBar();
    startCartObserver();
    scheduleRelatedLoad();
  }

})();
